import type { CogoBlockDef } from "@cogo/types"

/**
 * Validate that no two logic/hybrid blocks claim the same namespace.
 * Returns a Map<namespace, blockName> for resolved namespaces.
 */
export function validateNamespaces(
  blocks: Record<string, CogoBlockDef>,
): Map<string, string> {
  const nsMap = new Map<string, string>()

  for (const [blockName, block] of Object.entries(blocks)) {
    const manifest = block.manifest
    if (!manifest) continue
    if (manifest.type === "ui") continue

    const ns = manifest.namespace
    if (!ns) continue

    if (nsMap.has(ns)) {
      throw new Error(
        `Namespace collision: #${ns} (claimed by "${nsMap.get(ns)}" and "${blockName}")`,
      )
    }
    nsMap.set(ns, blockName)
  }

  return nsMap
}

/**
 * Resolve UI block binds — verify each bind target exists as a registered namespace.
 * Returns a Map<blockName, allowedNamespaces[]>.
 */
export function resolveBindings(
  blocks: Record<string, CogoBlockDef>,
  nsMap: Map<string, string>,
): Map<string, string[]> {
  const bindingsMap = new Map<string, string[]>()

  for (const [blockName, block] of Object.entries(blocks)) {
    const manifest = block.manifest
    if (!manifest) continue
    if (!manifest.binds || manifest.binds.length === 0) continue

    for (const ns of manifest.binds) {
      if (!nsMap.has(ns)) {
        throw new Error(
          `Block "${blockName}" binds to unknown namespace: #${ns}`,
        )
      }
    }
    bindingsMap.set(blockName, [...manifest.binds])
  }

  return bindingsMap
}
